import { GiphyFetch } from '@giphy/js-fetch-api';
import firebase from 'firebase';
import { get, isEmpty, last, map, some, trim, toLower, zipWith, filter } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useAsync } from 'react-async-hook';
import { useCookies } from 'react-cookie';
import { useCollection, useDocument, useDocumentData } from 'react-firebase-hooks/firestore';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { GameStatus } from './Game';
import { getTerm } from './terms';

const useQuery = () => new URLSearchParams(useLocation().search);

const GIPHY_API_KEY = 'tsXtJEe36sGogFcPCgWY6lE1fbCjCmbm';
const gf = new GiphyFetch(GIPHY_API_KEY);

const NUM_GIFS = 20;

export const useGiphy = (term: string) => {
  const { result } = useAsync(() => gf.search(term, { sort: 'relevant', limit: NUM_GIFS }), [term]);
  return get(result?.data, Math.floor(Math.random() * NUM_GIFS));
};

export const useGameContext = (): {
  roomId?: string,
  gameId?: string,
  roundId?: string,
  playerId?: string,
} => {
  const [cookies] = useCookies(['nua-oans']) ;
  const { roomId, playerId } = cookies['nua-oans'] || {};
  const { gameId, roundId } = useParams();
  return { roomId, gameId, roundId, playerId };
};

export const useRoom = () => {
  const history = useHistory();
  const { roomId, playerId } = useGameContext();
  const [room, loading, error] = useDocument(
    firebase.firestore().doc(`Room/${roomId}`)
  );
  const leaveRoom = useCallback(
    async () => {
      const playerRef = await firebase.firestore().doc(`Room/${roomId}/Player/${playerId}`);
      await playerRef.update({ active: false });
      history.push('/');
    },
    [roomId, playerId]
  );
  return { room, leaveRoom, loading, error };
};

export const usePlayers = () => {
  const { roomId } = useGameContext();
  const [players, loading, error] = useCollection(
    firebase.firestore().collection(`Room/${roomId}/Player`)
  );
  const activePlayers = filter(players?.docs, p => p.get('active') as boolean);
  return { players, activePlayers, loading, error };
};

export type Player = {
  name: string,
  owner: boolean,
  avatar?: string,
  active: boolean,
};

export const usePlayer = () => {
  const { roomId, playerId } = useGameContext();
  const [player, loading, error] = useDocument(
    firebase.firestore().doc(`Room/${roomId}/Player/${playerId}`)
  );
  const { room, loading: roomLoading, error: roomError} = useRoom();
  const roomCreatedByPath = room?.data()?.createdBy?.path;
  const isRoomOwner = roomCreatedByPath && roomCreatedByPath === player?.ref?.path;
  console.log({ roomCreatedByPath, player: player?.ref?.path, isRoomOwner });
  return { player, isRoomOwner, loading: loading || roomLoading, error: error || roomError };
};

export const useGames = () => {
  const { roomId } = useGameContext();
  const [games, loading, error] = useCollection(
    firebase.firestore().collection(`Room/${roomId}/Game`).orderBy('index')
  );
  return { games, loading, error };
};

export const useGame = () => {
  const { roomId, gameId } = useGameContext();
  const history = useHistory();
  const [game, loading, error] = useDocument(
    firebase.firestore().doc(`Room/${roomId}/Game/${gameId}`)
  );
  const { rounds } = useRounds();
  const { activePlayers } = usePlayers();
  const { isRoomOwner } = usePlayer();

  const isFinished = rounds?.docs && rounds.docs.length >= 13;
  const statusLastRound = last(rounds?.docs)?.get('status');
  const canCreateRound =
    !isFinished && (isEmpty(rounds?.docs) || isRoundStatusEnded(statusLastRound));
  const canRepeatRound =
    isRoomOwner && !isEmpty(rounds?.docs) && !isRoundStatusEnded(statusLastRound);

  const createRound = useCallback(
    async () => {
      if (canCreateRound) {
        const index = (rounds?.docs.length || 0) + 1;
        await firebase.firestore().doc(`Room/${roomId}/Game/${gameId}/Round/${index}`).set({
          index,
          status: RoundStatus.COLLECTING_HINTS,
          guesser: activePlayers[index % activePlayers.length].ref,
          moderator: activePlayers[(index + 1) % activePlayers.length].ref,
          term: getTerm(),
        });
        history.push(`/room/game/${gameId}/round/${index}`);
      }
    },
    [canCreateRound, rounds, activePlayers, roomId, gameId]
  );

  const repeatRound = useCallback(
    async () => {
      if (canRepeatRound) {
        const index = rounds?.docs.length || 0;
        await firebase.firestore().doc(`Room/${roomId}/Game/${gameId}/Round/${index}`).set({
          index,
          status: RoundStatus.COLLECTING_HINTS,
          guesser: activePlayers[index % activePlayers.length].ref,
          moderator: activePlayers[(index + 1) % activePlayers.length].ref,
          term: getTerm(),
        });
      }
    },
    [canRepeatRound, rounds, activePlayers, roomId, gameId]
  );
  
  return { game, createRound, canCreateRound, repeatRound, canRepeatRound, isFinished, loading, error };
};

export const useRounds = () => {
  const { roomId, gameId } = useGameContext();
  const [rounds, loading, error] = useCollection(
    firebase.firestore().collection(`Room/${roomId}/Game/${gameId}/Round`).orderBy('index')
  );
  return { rounds, loading, error };
};

export enum RoundStatus {
  COLLECTING_HINTS = 'collecting_hints',
  GUESSING = 'guessing',
  NO_GUESS = 'no_guess',
  CORRECT = 'correct',
  WRONG = 'wrong',
}

const isRoundStatusEnded = (status: RoundStatus) =>
  RoundStatus.CORRECT === status || RoundStatus.WRONG === status || RoundStatus.NO_GUESS === status;

type Round = {
  index: number,
  status?: RoundStatus,
  term?: string,
  guess?: string,
  guesser?: firebase.firestore.DocumentReference,
  moderator?: firebase.firestore.DocumentReference,
};

export const useRound = () => {
  const { roomId, gameId, roundId, playerId } = useGameContext();
  const history = useHistory();
  const [player, playerLoading, playerError] = useDocument(
    firebase.firestore().doc(`Room/${roomId}/Player/${playerId}`)
  );
  const [game] = useDocument(
    firebase.firestore().doc(`Room/${roomId}/Game/${gameId}`)
  );
  const { activePlayers } = usePlayers();
  const [rounds] = useCollection(
    firebase.firestore().collection(`Room/${roomId}/Game/${gameId}/Round`).orderBy('index')
  );
  const [round, loading, error] = useDocumentData<Round>(
    firebase.firestore().doc(`Room/${roomId}/Game/${gameId}/Round/${roundId}`)
  );
  const [guesser] = useDocumentData<Player>(round?.guesser);
  const [moderator] = useDocumentData<Player>(round?.moderator);
  const isGuesser = round?.guesser && player && round.guesser.isEqual(player.ref);
  const isModerator = round?.moderator && player && round.moderator.isEqual(player.ref);
  const [hints, hintsLoading, hintsError] = useCollection(
    firebase.firestore().collection(`Room/${roomId}/Game/${gameId}/Round/${roundId}/Hint`)
  );
  const hintSubmitted = hints && player && some(hints.docs, doc => doc.data().player.isEqual(player.ref));

  const addHint = useCallback(
    async (term: string) => {
      if (player) {
        await firebase.firestore().collection(`Room/${roomId}/Game/${gameId}/Round/${roundId}/Hint`).add({
          term: trim(term),
          player: player.ref,
        });
      }
    },
    [roomId, gameId, roundId, player]
  );

  const discoverGuesses = useCallback(
    async () => {
      await firebase.firestore().doc(`Room/${roomId}/Game/${gameId}/Round/${roundId}`).update({
        status: RoundStatus.GUESSING,
      })
    },
    [roomId, gameId, roundId]
  );

  const onGuess = useCallback(
    async (guess: string) => {
      await firebase.firestore().doc(`Room/${roomId}/Game/${gameId}/Round/${roundId}`).update({
        guess: trim(guess),
        status: toLower(trim(guess)) === toLower(trim(round?.term)) ? RoundStatus.CORRECT : RoundStatus.WRONG,
      });
    },
    [roomId, gameId, roundId, round]
  );

  const onNoGuess = useCallback(
    async () => {
      await firebase.firestore().doc(`Room/${roomId}/Game/${gameId}/Round/${roundId}`).update({
        status: RoundStatus.NO_GUESS,
      });
    },
    [roomId, gameId, roundId]
  );

  const nextRound = useCallback(
    async () => {
      const index = (rounds?.docs.length || 0);
      const statusLastRound = last(rounds?.docs)?.get('status');
      if (isRoundStatusEnded(statusLastRound)) {
        if (index >= 13) {
          console.log('no new round', index);
          if (game?.get('status') === GameStatus.RUNNING) {
            await game.ref.update({
              status: GameStatus.ENDED
            });
          }
          history.push(`/room/game/${gameId}`);
        } else {
          const newIndex = index + 1;
          await firebase.firestore().doc(`Room/${roomId}/Game/${gameId}/Round/${newIndex}`).set({
            index: newIndex,
            status: RoundStatus.COLLECTING_HINTS,
            guesser: activePlayers[newIndex % activePlayers.length].ref,
            moderator: activePlayers[(newIndex + 1) % activePlayers.length].ref,
            term: getTerm(),
          });            
          history.push(`/room/game/${gameId}/round/${newIndex}`);
        }
      } else {
        history.push(`/room/game/${gameId}/round/${index}`);
      }
   },
   [rounds, activePlayers, roomId, gameId, playerId]
  );

  return {
    round,
    moderator, guesser,
    isGuesser, isModerator, hintSubmitted,
    addHint, discoverGuesses, onGuess, onNoGuess, nextRound,
    loading, error,
  };
};

type Hint = {
  player: {
    name: string,
    owner: boolean,
  },
  term: string,
};

export const useHints = () => {
  const { roomId, gameId, roundId } = useGameContext();
  const [rawHints, loading, error] = useCollection(
    firebase.firestore().collection(`Room/${roomId}/Game/${gameId}/Round/${roundId}/Hint`)
  );
  const hintsData = useMemo(
    () => map(rawHints?.docs, doc => doc.data()),
    [rawHints]
  );
  const players = useAsync(() => Promise.all(map(hintsData, ({ player }) => player.get())), [rawHints]);
  const hints = useMemo(
    () => {
      const playersData = map(players.result, ds => ds.data());
      return zipWith(rawHints?.docs, hintsData, playersData, (doc, hint: any, player: any) => ({ doc, ...hint, player }));
    },
    [rawHints, hintsData, players]
  )
  return { hints, loading, error };
};
